import apiClient from './client';

// ─── Typen ──────────────────────────────────────────────────────────────────

/** Berufsstation einer Person (Antwortformat: camelCase wie alle Prisma-Responses). */
export interface Occupation {
  id: string;
  personId: string;
  title: string;
  employer: string | null;
  place: string | null;
  fromText: string | null;
  fromYear: number | null;
  toText: string | null;
  toYear: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Eingabeformat (Request-Bodies sind snake_case wie überall im Backend). */
export interface OccupationInput {
  title: string;
  employer?: string | null;
  place?: string | null;
  from_text?: string | null;
  to_text?: string | null;
  notes?: string | null;
}

export interface Biography {
  id: string;
  personId: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export type ChronicleEventType =
  | 'birth'
  | 'occupation'
  | 'marriage'
  | 'separation'
  | 'child'
  | 'death';

export interface ChronicleEvent {
  type: ChronicleEventType;
  year: number | null;
  dateText: string | null;
  title: string;
  detail: string | null;
  relatedPersonId: string | null;
}

export interface Chronicle {
  person: { id: string; firstName: string; lastName: string | null };
  events: ChronicleEvent[];
}

// ─── Berufslaufbahn ─────────────────────────────────────────────────────────

export async function getOccupations(personId: string): Promise<Occupation[]> {
  const { data } = await apiClient.get<Occupation[]>(`/persons/${personId}/occupations`);
  return data;
}

export async function createOccupation(personId: string, input: OccupationInput): Promise<Occupation> {
  const { data } = await apiClient.post<Occupation>(`/persons/${personId}/occupations`, input);
  return data;
}

export async function updateOccupation(
  personId: string,
  occupationId: string,
  input: Partial<OccupationInput>,
): Promise<Occupation> {
  const { data } = await apiClient.patch<Occupation>(
    `/persons/${personId}/occupations/${occupationId}`,
    input,
  );
  return data;
}

export async function deleteOccupation(personId: string, occupationId: string): Promise<void> {
  await apiClient.delete(`/persons/${personId}/occupations/${occupationId}`);
}

// ─── Lebenslauf (Erzähltext + generierte Chronik) ───────────────────────────

export interface BiographyListEntry {
  id: string;
  personId: string;
  updatedAt: string;
  person: {
    id: string;
    firstName: string;
    lastName: string | null;
    birthDateText: string | null;
    deathDateText: string | null;
  };
}

/** Alle Personen mit gepflegtem Lebenslauf-Text (fürs Archiv, neueste zuerst). */
export async function listBiographies(): Promise<BiographyListEntry[]> {
  const { data } = await apiClient.get<BiographyListEntry[]>('/persons/biographies');
  return data;
}

export async function getBiography(personId: string): Promise<Biography | null> {
  const { data } = await apiClient.get<Biography | null>(`/persons/${personId}/biography`);
  return data;
}

/** Leerer Text löscht den Lebenslauf-Text (Antwort: null). */
export async function upsertBiography(personId: string, text: string): Promise<Biography | null> {
  const { data } = await apiClient.put<Biography | null>(`/persons/${personId}/biography`, { text });
  return data;
}

export async function getChronicle(personId: string): Promise<Chronicle> {
  const { data } = await apiClient.get<Chronicle>(`/persons/${personId}/chronicle`);
  return data;
}
